import Suspense from '@/Components/Suspense';
import { SvgIntegrationsHubsotIcon } from '@/assets/images/integrations';
import { HsInfoQuery, type Integration_HubspotInfo_Query } from '@/graphql/queries/Integration.query';
import { Alert, Card, CardActionArea, Grid, Skeleton } from '@mui/material';
import { createLink } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { type PreloadedQuery, usePreloadedQuery, useQueryLoader } from 'react-relay';

const SCOPES = Object.freeze([
  'crm.export',
  'crm.schemas.companies.read',
  'crm.objects.contacts.read',
  'crm.objects.contacts.write',
  'crm.objects.users.read',
  'crm.objects.companies.read',
  'crm.objects.owners.read',
  'crm.schemas.contacts.read',
  'crm.schemas.contacts.write',
  'e-commerce',
]);
export { SCOPES as HUBSPOT_SCOPES };

const MuiCardActionArea = createLink(CardActionArea);

const CardComponent = (props: {
  hsInfoRef: PreloadedQuery<Integration_HubspotInfo_Query, Record<string, unknown>>;
}) => {
  const { hsInfo } = usePreloadedQuery<Integration_HubspotInfo_Query>(HsInfoQuery, props.hsInfoRef);

  const CardType = useMemo(() => {
    if (!hsInfo?.isValidToken) {
      return 'no-auth';
    }

    if (!SCOPES.every((scope) => hsInfo?.scopes?.includes(scope))) {
      return 'missing-scopes';
    }

    return 'connected';
  }, [hsInfo?.isValidToken, hsInfo?.scopes]);

  return (
    <Grid size={{ sm: 12, md: 6 }}>
      <Card>
        <MuiCardActionArea to="/dash/settings/management/integrations/hubspot" sx={{ p: 1 }}>
          <SvgIntegrationsHubsotIcon style={{ width: '100%', height: '45px' }} />
          <p>Connect Zngly to Hubspot for deeper analytics insights. </p>

          {CardType === 'no-auth' && <Alert severity="error">Please connect to HubSpot</Alert>}
          {CardType === 'missing-scopes' && (
            <Alert severity="warning">
              Missing Permissions. Please reconnect to HubSpot to ensure integration is working correctly.
            </Alert>
          )}
          {CardType === 'connected' && <Alert severity="success">Connected to HubSpot</Alert>}
        </MuiCardActionArea>
      </Card>
    </Grid>
  );
};

const Fallback = () => {
  return (
    <Grid size={{ sm: 12, md: 6 }}>
      <Card>
        <MuiCardActionArea to="/dash/settings/management/integrations/hubspot" sx={{ p: 1 }}>
          <SvgIntegrationsHubsotIcon style={{ width: '100%', height: '45px' }} />
          <p>Connect Zngly to Hubspot for deeper analytics insights. </p>
          <Skeleton variant="rectangular" sx={{ width: '100%', height: '80px' }} />
        </MuiCardActionArea>
      </Card>
    </Grid>
  );
};

const IntegrationCard = () => {
  const [hsInfoRef, loadHsInfo, disposeHsInfo] = useQueryLoader<Integration_HubspotInfo_Query>(HsInfoQuery);

  useEffect(() => {
    loadHsInfo({}, { fetchPolicy: 'store-and-network' });

    return () => {
      disposeHsInfo();
    };
  }, []);

  return (
    <Suspense fallback={<Fallback />} exists={hsInfoRef}>
      <CardComponent hsInfoRef={hsInfoRef!} />
    </Suspense>
  );
};

export default IntegrationCard;
